"use client";

import React, { useEffect, useRef, useTransition } from "react";
import { useQueryStates } from "nuqs";
import { Button } from "@/app/components/ui/button";
import { searchParams } from "@/app/types/search-params";
import { setTenantStandortResetCookie } from "./tenant-location-reset";

type ResetFiltersQueryStatesT = {
  name: string;
  marke: string[];
  abteilung: string[];
  standort: string[];
  kategorie: string[];
  seite: number;
  tenantStandortReset: string;
};

export type ResetFiltersButtonProps = {
  keys: string[];
  className?: string;
  label?: string;
  additionalQueryUpdates?: Partial<ResetFiltersQueryStatesT>;
  resetTenantStandortDefault?: boolean;
};

export default function ResetFiltersButton({
  keys,
  className,
  label = "Filter zurücksetzen",
  additionalQueryUpdates,
  resetTenantStandortDefault = false,
}: ResetFiltersButtonProps) {
  const [isPending, startTransition] = useTransition();

  // Prepare a superset of parsers so we can batch-reset optimistically
  const [, setQueryStates] = useQueryStates(
    {
      // strings
      name: searchParams.name,
      // arrays (german only)
      marke: searchParams.marke,
      abteilung: searchParams.abteilung,
      standort: searchParams.standort,
      kategorie: searchParams.kategorie,
      // pagination
      seite: searchParams.seite,
      tenantStandortReset: searchParams.tenantStandortReset,
    },
    { shallow: false, startTransition }
  );

  // Avoid scroll jump: capture and restore scrollY around transition
  const scrollRef = useRef<number>(0);
  useEffect(() => {
    if (!isPending && scrollRef.current > 0) {
      window.scrollTo({ top: scrollRef.current });
      scrollRef.current = 0;
    }
  }, [isPending]);

  function onReset() {
    // Capture position before triggering navigation
    scrollRef.current = window.scrollY;

    type StringKey = "name";
    type ArrayKey = "marke" | "abteilung" | "standort" | "kategorie";
    type NumberKey = "seite";

    const updates: Partial<ResetFiltersQueryStatesT> = {};

    const stringKeys: readonly StringKey[] = ["name"] as const;
    const arrayKeys: readonly ArrayKey[] = [
      "marke",
      "abteilung",
      "standort",
      "kategorie",
    ] as const;
    const numberKeys: readonly NumberKey[] = ["seite"] as const;

    for (const k of stringKeys) {
      if (keys.includes(k)) updates[k] = "";
    }
    for (const k of arrayKeys) {
      if (keys.includes(k)) updates[k] = [];
    }
    for (const k of numberKeys) {
      if (keys.includes(k)) updates[k] = 1;
    }

    if (resetTenantStandortDefault) {
      setTenantStandortResetCookie();
      updates.tenantStandortReset = "";
    }

    Object.assign(updates, additionalQueryUpdates);

    setQueryStates(updates);
  }

  return (
    <Button
      type="button"
      variant="secondary"
      className={className}
      onClick={onReset}
      disabled={isPending}
    >
      {isPending && (
        <span
          aria-hidden
          className="border-primary mr-2 inline-block size-4 animate-spin rounded-full border-2 border-t-transparent align-[-2px]"
        />
      )}
      {label}
    </Button>
  );
}
